import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '@SonarCX/store'

import { getPinnedTabs, savePinnedTabs } from './helpers'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { listenerMiddleware } from '@SonarCX/store/listenerMiddleware'

export type PinnedTab = {
  id: string
  title: string
  type: 'note' | 'team-chat' | 'task' | 'contact' | 'team-member' | 'article'
} & Record<string, any>

type PinnedTabsState = {
  tabs: PinnedTab[]
  dbLoaded: boolean
  saveParams: [string, string]
}

const initialState: PinnedTabsState = {
  tabs: [],
  dbLoaded: false,
  saveParams: ['', '']
}

const pinnedTabsSlice = createSlice({
  name: 'pinnedTabs',
  initialState,
  reducers: {
    setSaveParams: (
      state: PinnedTabsState,
      action: PayloadAction<[string, string]>
    ) => {
      state.saveParams = action.payload
    },

    finishLoading: (
      state: PinnedTabsState,
      action: PayloadAction<PinnedTab[]>
    ) => {
      state.tabs = action.payload
      state.dbLoaded = true
    },

    addTab: (state: PinnedTabsState, action: PayloadAction<PinnedTab>) => {
      if (state.tabs.some(tab => tab.id === action.payload.id)) {
        return
      }

      state.tabs.push(action.payload)
    },

    removeTab: (state: PinnedTabsState, action: PayloadAction<string>) => {
      state.tabs = state.tabs.filter(tab => tab.id !== action.payload)
    }
  }
})

export const { setSaveParams, addTab, removeTab } = pinnedTabsSlice.actions
const { finishLoading } = pinnedTabsSlice.actions

export const loadPinnedTabs = (userID: string, envName: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setSaveParams([userID, envName]))

    try {
      const tabs = await getPinnedTabs(userID, envName)
      dispatch(finishLoading(tabs))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectPinnedTabs = (state: RootState) => state.pinnedTabs

export default pinnedTabsSlice.reducer

listenerMiddleware.startListening({
  predicate: (action, _currState, _prevState) => {
    return [addTab.type, removeTab.type].includes(action.type)
  },

  effect: (_action, listenerApi) => {
    const getState = listenerApi.getState as GetState
    const { saveParams, tabs } = selectPinnedTabs(getState())

    const [userID, envName] = saveParams
    savePinnedTabs(userID, envName, tabs)
  }
})
